import { useMemo } from 'react'
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isSameMonth, format, addMonths, subMonths } from 'date-fns'
import { zhTW, enUS, ja, ko, de } from 'date-fns/locale'
import { Button } from './button'

const LOCALES: Record<string, typeof zhTW> = { 'zh-TW': zhTW, en: enUS, ja, ko, de }

interface CalendarProps {
  value: Date
  onChange: (date: Date) => void
  locale?: string
}

/** 月份日历网格，无外部依赖，复用 date-fns */
export function Calendar({ value, onChange, locale = 'zh-TW' }: CalendarProps) {
  const loc = LOCALES[locale] || enUS
  const monthStart = startOfMonth(value)
  const monthEnd = endOfMonth(value)
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
  const days = useMemo(() => eachDayOfInterval({ start: calStart, end: calEnd }), [value.getTime()])

  const weekDays = useMemo(() => {
    const base = startOfWeek(new Date(2024, 0, 1), { weekStartsOn: 1 })
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(d.getDate() + i)
      return format(d, 'EEEEE', { locale: loc })
    })
  }, [loc])

  return (
    <div className="p-2 min-w-[224px]" data-testid="calendar">
      <div className="flex items-center justify-between mb-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(subMonths(value, 1))} data-testid="calendar-prev">
          ‹
        </Button>
        <span className="text-sm font-medium">{format(value, 'yyyy/M', { locale: loc })}</span>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(addMonths(value, 1))} data-testid="calendar-next">
          ›
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {weekDays.map((d) => (
          <div key={d} className="text-xs text-slate-400 py-1">{d}</div>
        ))}
        {days.map((day) => {
          const isToday = isSameDay(day, new Date())
          const isSelected = isSameDay(day, value)
          const isOutside = !isSameMonth(day, value)
          return (
            <button
              key={day.toISOString()}
              type="button"
              data-testid={isSelected ? 'calendar-selected' : undefined}
              className={`text-sm rounded w-7 h-7 flex items-center justify-center
                ${isSelected ? 'bg-slate-900 text-white' : isToday ? 'border border-slate-400' : 'hover:bg-slate-100'}
                ${isOutside ? 'text-slate-300' : ''}`}
              onClick={() => onChange(day)}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>
    </div>
  )
}
